/**
 * The briefing's "Coming Up" lines are sentences the model writes ("Evidence
 * collection due for [Amara Okafor](…) on 2026-10-24."). Find the date in
 * each so the view can show it as a dated row with the days remaining; a
 * trailing "on <date>" / "by <date>" moves into the date column. Lines
 * without a readable date stay as they are.
 */
import dayjs, { type Dayjs } from "dayjs";

export type ComingUpRow = {
  /** The line, minus a trailing date phrase when one was lifted out. */
  text: string;
  date: Date | null;
};

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec"
];

const ISO = String.raw`(\d{4})-(\d{2})-(\d{2})`;
const NAMED = String.raw`(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?`;
const DATE_RE = new RegExp(`${ISO}|\\b${NAMED}\\b`, "i");
const TRAILING_RE = new RegExp(
  String.raw`(?:,\s*|\s+)(?:on|by|due)?\s*\(?(?:${ISO}|${NAMED})\)?\s*\.?\s*$`,
  "i"
);

/** A month/day without a year lands on its next occurrence (a week of slack back). */
const resolveYear = (month: number, day: number, now: Dayjs): Dayjs => {
  let d = now.month(month).date(day).startOf("day");
  if (d.isBefore(now.startOf("day").subtract(7, "day"))) d = d.add(1, "year");
  return d;
};

const parseDate = (m: RegExpMatchArray, now: Dayjs): Date | null => {
  if (m[1]) {
    const d = dayjs(`${m[1]}-${m[2]}-${m[3]}`);
    return d.isValid() ? d.toDate() : null;
  }
  const month = MONTHS.indexOf(m[4].slice(0, 3).toLowerCase());
  const day = Number(m[5]);
  if (month < 0 || day < 1 || day > 31) return null;
  if (m[6]) {
    const d = dayjs(new Date(Number(m[6]), month, day));
    return d.isValid() ? d.toDate() : null;
  }
  return resolveYear(month, day, now).toDate();
};

export const comingUpRows = (
  lines: string[],
  now: Date = new Date()
): ComingUpRow[] => {
  const n = dayjs(now);
  return lines.map((line) => {
    const found = line.match(DATE_RE);
    if (!found) return { text: line, date: null };
    const date = parseDate(found, n);
    if (!date) return { text: line, date: null };
    const trailing = line.match(TRAILING_RE);
    return {
      text: trailing ? line.slice(0, trailing.index).trimEnd() : line,
      date
    };
  });
};

/** "in 12 days" / "tomorrow" / "today" / "3 days ago". */
export const daysAway = (date: Date, now: Date = new Date()): string => {
  const d = dayjs(date).startOf("day").diff(dayjs(now).startOf("day"), "day");
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d === -1) return "yesterday";
  return d > 0 ? `in ${d} days` : `${-d} days ago`;
};
